import MathUtils from '../math/MathUtils.js';
import Transform from '../math/Transform.js';
import VirtualJoystick from './VirtualJoystick.js';

/**
 * Manages keyboard, mouse, and touch input
 */
export default class InputManager {
    /**
     * Create a new input manager
     * @param {Camera} camera - The camera
     */
    constructor(camera) {
      this.camera = camera;
      this.keys = {
        'w': false, 'a': false, 's': false, 'd': false,
        'o': false, 'p': false,
        ' ': false, 'c': false, // Spacebar = up, C = down
        'e': false, 'r': false, 'f': false, // Attack keys
        'ArrowUp': false, 'ArrowDown': false,
        'zoomIn': false, 'zoomOut': false,
        'Shift': false // Run modifier
      };
      
      this.touchState = {
        mode: '', // 'potential_drag', 'drag', or 'pinch'
        initialDistance: null,
        initialZoom: null,
        lastX: null,
        lastY: null,
        startX: null,
        startY: null,
        // For two-finger pan, track center point
        lastCenterX: null,
        lastCenterY: null,
        // Track which touch is being used for camera control
        cameraTouchId: null,
        // Track which touches are being used for pinch
        pinchTouchIds: null,
        // Skip first frame flags to avoid jumps during mode transitions
        pinchFirstFrame: false,
        dragFirstFrame: false
      };

      this.mouseState = {
        isDragging: false,
        hasMoved: false, // Track if mouse moved significantly (to distinguish click vs drag)
        button: null, // 0 = left, 1 = middle, 2 = right
        startX: null,
        startY: null,
        lastX: null,
        lastY: null,
        wasRightDrag: false // True briefly after a right-click drag ends, to suppress contextmenu
      };

      this.setupKeyboardControls();
      this.setupButtonControls();
      this.setupMouseControls();
      this.setupTouchControls();

      // Create virtual joystick for mobile character movement
      this.virtualJoystick = new VirtualJoystick(this);
    }
    
    /**
     * Set up keyboard controls
     */
    setupKeyboardControls() {
      // Check if user is typing in an input field
      const isTyping = () => {
        const el = document.activeElement;
        if (el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.isContentEditable)) return true;
        // Block game input when dialog is active
        if (window.game?.dialogManager?.isActive) return true;
        return false;
      };

      // Normalize key - lowercase for letters, preserve special keys like Shift, Arrow*, etc.
      const normalizeKey = (key) => {
        if (!key) return '';
        if (key.length === 1 && key >= 'A' && key <= 'Z') {
          return key.toLowerCase();
        }
        return key;
      };

      window.addEventListener('keydown', (e) => {
        // Prevent browser zoom with Ctrl+Plus/Minus
        if (e.ctrlKey && (e.key === '+' || e.key === '=' || e.key === '-')) {
          e.preventDefault();
        }

        // Don't handle camera keys when typing in input fields
        if (isTyping()) return;

        const key = normalizeKey(e.key);

        if (key === '+' || key === '=') {
          this.keys.zoomIn = true;
        } else if (key === '-') {
          this.keys.zoomOut = true;
        } else if (this.keys.hasOwnProperty(key) && !e.repeat) {
          this.keys[key] = true;
          // Prevent spacebar from scrolling the page
          if (key === ' ') e.preventDefault();
        }
      });

      window.addEventListener('keyup', (e) => {
        const key = normalizeKey(e.key);

        // Always handle keyup to reset state even if focus changed
        if (key === '+' || key === '=')
          this.keys.zoomIn = false;
        else if (key === '-')
          this.keys.zoomOut = false;
        else if (this.keys.hasOwnProperty(key))
          this.keys[key] = false;
      });

      // When window loses focus (e.g., iframe captures it), release all keys
      // to prevent stuck movement from missed keyup events
      window.addEventListener('blur', () => {
        for (const key in this.keys) {
          this.keys[key] = false;
        }
      });
    }
    
    /**
     * Set up UI button controls
     */
    setupButtonControls() {
      // Utility for continuous press events
      const addButtonControl = (id, keyName) => {
        const btn = document.getElementById(id);
        if (!btn) return;
        
        btn.addEventListener('mousedown', () => { this.keys[keyName] = true; });
        btn.addEventListener('touchstart', (e) => { e.preventDefault(); this.keys[keyName] = true; });
        btn.addEventListener('mouseup', () => { this.keys[keyName] = false; });
        btn.addEventListener('mouseleave', () => { this.keys[keyName] = false; });
        btn.addEventListener('touchend', () => { this.keys[keyName] = false; });
        btn.addEventListener('touchcancel', () => { this.keys[keyName] = false; });
      };
      
      // Rotation controls
      addButtonControl('rotateLeft', 'o');
      addButtonControl('rotateRight', 'p');
      
      // Skew controls
      addButtonControl('skewUp', 'ArrowUp');
      addButtonControl('skewDown', 'ArrowDown');
      
      // Zoom controls
      addButtonControl('zoomIn', 'zoomIn');
      addButtonControl('zoomOut', 'zoomOut');
      
      // Camera movement controls
      addButtonControl('camUp', 'w');
      addButtonControl('camDown', 's');
      addButtonControl('camLeft', 'a');
      addButtonControl('camRight', 'd');
      
      // Reset button
      const zoomReset = document.getElementById('zoomReset');
      if (zoomReset) {
        zoomReset.addEventListener('mousedown', () => {
          this.camera.reset();
        });
        zoomReset.addEventListener('touchstart', (e) => {
          e.preventDefault();
          this.camera.reset();
        });
      }
    }
    
    /**
     * Set up mouse controls for drag pan, wheel zoom, and right-click rotate
     */
    setupMouseControls() {
      const containerEl = document.getElementById('container');
      if (!containerEl) return;

      const DRAG_THRESHOLD = 5; // Pixels of movement before considering it a drag

      // Create mouse drag overlay for right-click drag (covers iframes)
      this.mouseDragOverlay = document.createElement('div');
      this.mouseDragOverlay.id = 'mouse-drag-overlay';
      this.mouseDragOverlay.style.cssText = `
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        z-index: 9998;
        display: none;
      `;
      containerEl.appendChild(this.mouseDragOverlay);

      // Prevent context menu on right-click.
      // Use document-level listener so it's blocked even when releasing over UI panels
      // (e.g. sidebar) that live outside #container. This prevents the browser context
      // menu from appearing mid-drag and stealing focus / stalling key states.
      // Block context menu globally — this is a game, not a regular website
      document.addEventListener('contextmenu', (e) => {
        e.preventDefault();
        this.mouseState.wasRightDrag = false;
      });

      // Mouse down - start drag (use capture to get event before FacePicker)
      containerEl.addEventListener('mousedown', (e) => {
        // Skip if clicking on UI elements
        if (e.target.closest('.coords, .info-panel, .gui-panel, .sidebar-panel, .top-toolbar, .top-sheet, .settings-panel')) return;

        this.mouseState.isDragging = true;
        this.mouseState.hasMoved = false;
        this.mouseState.button = e.button;
        this.mouseState.startX = e.clientX;
        this.mouseState.startY = e.clientY;
        this.mouseState.lastX = e.clientX;
        this.mouseState.lastY = e.clientY;

        // Show overlay for right-click drag to capture events over iframes
        if (e.button === 2) {
          this.mouseDragOverlay.style.display = 'block';
        }
      }, true); // Use capture phase


      // Mouse move handler (shared between window and overlay)
      const handleMouseMove = (e) => {
        if (!this.mouseState.isDragging) return;

        const dx = e.clientX - this.mouseState.lastX;
        const dy = e.clientY - this.mouseState.lastY;

        // Check if we've moved enough to be considered a drag
        const totalDx = e.clientX - this.mouseState.startX;
        const totalDy = e.clientY - this.mouseState.startY;
        if (Math.abs(totalDx) > DRAG_THRESHOLD || Math.abs(totalDy) > DRAG_THRESHOLD) {
          this.mouseState.hasMoved = true;
        }

        if (this.mouseState.hasMoved) {
          if (this.mouseState.button === 0) {
            // Left button: pan camera
            const offsetX = dx / this.camera.zoomLevel;
            const offsetY = dy / (this.camera.zoomLevel * this.camera.skewAngle);
            const rotation = Transform.getRotationMatrix(-this.camera.angle);

            this.camera.position.x -= (offsetX * rotation.cos - offsetY * rotation.sin);
            this.camera.position.z -= (offsetX * rotation.sin + offsetY * rotation.cos);
          } else if (this.mouseState.button === 2) {
            // Right button: rotate camera
            if (this.orbitalController && this.orbitalController.isEnabled()) {
              // Orbital mode: modify target angle for smooth interpolation
              this.orbitalController.targetOrbitAngle -= dx * 0.5;
            } else {
              // Free camera mode: direct angle control
              this.camera.angle -= dx * 0.5;
            }
            // Adjust skew with vertical movement (allow negative for below-horizon view)
            this.camera.skewAngle = MathUtils.clamp(this.camera.skewAngle + dy * 0.005, this.camera.minSkewAngle !== undefined ? this.camera.minSkewAngle : -2, 2);
          }
        }

        this.mouseState.lastX = e.clientX;
        this.mouseState.lastY = e.clientY;
      };

      // Mouse move - drag to pan or rotate
      window.addEventListener('mousemove', handleMouseMove);
      this.mouseDragOverlay.addEventListener('mousemove', handleMouseMove);

      // Mouse up handler (shared)
      const handleMouseUp = (e) => {
        // Track if this was a right-click drag so we can suppress the contextmenu
        // event that fires immediately after mouseup
        this.mouseState.wasRightDrag = this.mouseState.button === 2 && this.mouseState.hasMoved;
        this.mouseState.isDragging = false;
        this.mouseState.button = null;
        // Hide overlay
        this.mouseDragOverlay.style.display = 'none';

        // Restore pointer events on any webpage containers/iframes that were disabled during drag
        document.querySelectorAll('.webpage-container').forEach(container => {
          container.style.pointerEvents = 'auto';
          const iframe = container.querySelector('iframe');
          if (iframe) iframe.style.pointerEvents = 'auto';
        });
      };

      // Mouse up - stop drag
      window.addEventListener('mouseup', handleMouseUp);
      this.mouseDragOverlay.addEventListener('mouseup', handleMouseUp);

      // Mouse wheel handler (shared between container and overlay)
      const handleWheel = (e) => {
        e.preventDefault();

        const zoomFactor = 1.1;
        if (this.orbitalController && this.orbitalController.isEnabled()) {
          // Orbital mode: modify target zoom for smooth interpolation
          const currentTarget = this.orbitalController.targetZoomLevel;
          if (e.deltaY < 0) {
            this.orbitalController.setTargetZoom(currentTarget * zoomFactor);
          } else {
            this.orbitalController.setTargetZoom(currentTarget / zoomFactor);
          }
        } else {
          // Free camera mode: direct zoom control
          if (e.deltaY < 0) {
            this.camera.zoomLevel = MathUtils.clamp(this.camera.zoomLevel * zoomFactor, this.camera.MIN_ZOOM, this.camera.MAX_ZOOM);
          } else {
            this.camera.zoomLevel = MathUtils.clamp(this.camera.zoomLevel / zoomFactor, this.camera.MIN_ZOOM, this.camera.MAX_ZOOM);
          }
        }
      };

      // Mouse wheel - zoom (prevent browser zoom with Ctrl+wheel)
      containerEl.addEventListener('wheel', handleWheel, { passive: false });
      this.mouseDragOverlay.addEventListener('wheel', handleWheel, { passive: false });

      // Prevent browser zoom from Ctrl+wheel globally
      window.addEventListener('wheel', (e) => {
        if (e.ctrlKey) {
          e.preventDefault();
        }
      }, { passive: false });

      // Prevent iframe scroll chaining from moving the parent.
      // When an iframe uses scrollIntoView() or scrollTo() (e.g. hash navigation),
      // the browser can chain the scroll to parent elements even with overflow:hidden.
      // Reset scroll position immediately to prevent the camera view from shifting.
      const preventParentScroll = (e) => {
        e.target.scrollTop = 0;
        e.target.scrollLeft = 0;
      };
      containerEl.addEventListener('scroll', preventParentScroll);
      document.addEventListener('scroll', preventParentScroll);
    }

    /**
     * Check if a drag is currently in progress (used by FacePicker)
     * @returns {boolean} True if dragging with movement
     */
    isDragging() {
      return this.mouseState.isDragging && this.mouseState.hasMoved;
    }

    /**
     * Set up touch controls for mobile
     * Uses a dynamic overlay approach: overlay only appears during active drag
     * to maintain touch tracking when DOM changes (subdivision).
     * Also uses Pointer Events with setPointerCapture for robust tracking.
     */
    setupTouchControls() {
      const containerEl = document.getElementById('container');
      if (!containerEl) return;

      // Store reference for overlay creation
      this.containerEl = containerEl;

      // Track captured pointer for Pointer Events API
      this.capturedPointerId = null;

      // Create overlay element (hidden by default)
      this.dragOverlay = document.createElement('div');
      this.dragOverlay.id = 'drag-overlay';
      this.dragOverlay.style.cssText = `
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        z-index: 9999;
        touch-action: none;
        display: none;
      `;
      containerEl.appendChild(this.dragOverlay);

      // Register overlay touch events for when it's active
      this.dragOverlay.addEventListener('touchmove', this.onTouchMove.bind(this), {passive: false});
      this.dragOverlay.addEventListener('touchend', this.onTouchEnd.bind(this), {passive: false});
      this.dragOverlay.addEventListener('touchcancel', this.onTouchEnd.bind(this), {passive: false});

      // Register Pointer Events on overlay for robust capture during drag
      // setPointerCapture ensures events continue even when DOM changes underneath
      this.dragOverlay.addEventListener('pointermove', this.onPointerMove.bind(this), {passive: false});
      this.dragOverlay.addEventListener('pointerup', this.onPointerEnd.bind(this), {passive: false});
      this.dragOverlay.addEventListener('pointercancel', this.onPointerEnd.bind(this), {passive: false});
      this.dragOverlay.addEventListener('lostpointercapture', this.onLostPointerCapture.bind(this), {passive: false});

      // Register container touch events for initial detection
      containerEl.addEventListener('touchstart', this.onTouchStart.bind(this), {passive: false});
      containerEl.addEventListener('touchmove', this.onTouchMove.bind(this), {passive: false});
      containerEl.addEventListener('touchend', this.onTouchEnd.bind(this), {passive: false});
      containerEl.addEventListener('touchcancel', this.onTouchEnd.bind(this), {passive: false});

      // Register pointerdown on container to capture the pointer when drag starts
      containerEl.addEventListener('pointerdown', this.onPointerDown.bind(this), {passive: false});

      // Prevent browser pinch-zoom globally
      document.addEventListener('touchmove', (e) => {
        if (e.touches.length > 1) {
          e.preventDefault();
        }
      }, { passive: false });

      // Prevent double-tap zoom
      document.addEventListener('touchend', (e) => {
        const now = Date.now();
        if (this._lastTouchEnd && now - this._lastTouchEnd < 300) {
          e.preventDefault();
        }
        this._lastTouchEnd = now;
      }, { passive: false });
    }

    /**
     * Show the drag overlay to capture touch events during active drag
     * Also captures pointer events for robust tracking through DOM changes
     */
    showDragOverlay() {
      if (this.dragOverlay) {
        this.dragOverlay.style.display = 'block';
        // Capture pointer if we have a pending pointer ID
        if (this.pendingPointerId !== null && this.dragOverlay.setPointerCapture) {
          try {
            this.dragOverlay.setPointerCapture(this.pendingPointerId);
            this.capturedPointerId = this.pendingPointerId;
          } catch (e) {
            // Pointer may have already ended
          }
          this.pendingPointerId = null;
        }
      }
    }

    /**
     * Hide the drag overlay when drag ends
     * Also releases any captured pointer
     */
    hideDragOverlay() {
      if (this.dragOverlay) {
        this.dragOverlay.style.display = 'none';
        // Release captured pointer
        if (this.capturedPointerId !== null && this.dragOverlay.releasePointerCapture) {
          try {
            this.dragOverlay.releasePointerCapture(this.capturedPointerId);
          } catch (e) {
            // Pointer may have already been released
          }
          this.capturedPointerId = null;
        }
      }
    }

    /**
     * Handle pointer down - track the pointer ID for potential capture
     * @param {PointerEvent} e - Pointer event
     */
    onPointerDown(e) {
      // Only track touch pointers (not mouse) for mobile drag fix
      if (e.pointerType !== 'touch') return;

      // Skip if on blocking UI
      const el = document.elementFromPoint(e.clientX, e.clientY);
      if (el && el.closest('.coords, .info-panel, .gui-panel, .sidebar-panel, .top-toolbar, .top-sheet, .settings-panel, .joystick-zone')) return;

      // Store pointer ID for capture when drag is confirmed
      this.pendingPointerId = e.pointerId;

      // Track pointer position for potential drag
      this.pointerState = {
        id: e.pointerId,
        startX: e.clientX,
        startY: e.clientY,
        lastX: e.clientX,
        lastY: e.clientY
      };
    }

    /**
     * Handle pointer move - process drag when captured
     * Only acts as FALLBACK when touch events have stopped (to avoid double-processing)
     * @param {PointerEvent} e - Pointer event
     */
    onPointerMove(e) {
      // Only handle if this is our captured pointer
      if (this.capturedPointerId === null || e.pointerId !== this.capturedPointerId) return;

      // Only process pointer events as FALLBACK when touch events have stopped.
      // If we received a touch event recently, skip pointer processing to avoid
      // double-processing camera movement. Use a short threshold (32ms ≈ 2 frames)
      // so pointer events take over quickly when touch dies (e.g., DOM changes
      // from cuboid subdivision removing the touch target).
      const now = performance.now();
      if (this.lastTouchMoveTime && (now - this.lastTouchMoveTime) < 32) {
        // Touch events are still active - just update position for sync
        if (this.pointerState && this.pointerState.id === e.pointerId) {
          this.pointerState.lastX = e.clientX;
          this.pointerState.lastY = e.clientY;
        }
        e.preventDefault();
        return;
      }

      // Touch events have stopped - pointer events take over as fallback
      if (this.pointerState && this.pointerState.id === e.pointerId) {
        const dx = e.clientX - this.pointerState.lastX;
        const dy = e.clientY - this.pointerState.lastY;

        // Cap delta to prevent wild rotation from edge cases
        const MAX_DELTA = 40;
        const clampedDx = MathUtils.clamp(dx, -MAX_DELTA, MAX_DELTA);
        const clampedDy = MathUtils.clamp(dy, -MAX_DELTA, MAX_DELTA);

        // Only apply camera movement in confirmed drag mode
        if (this.touchState.mode === 'drag') {
          if (this.orbitalController && this.orbitalController.isEnabled()) {
            this.orbitalController.targetOrbitAngle -= clampedDx * 0.5;
          } else {
            this.camera.angle -= clampedDx * 0.5;
          }
          this.camera.skewAngle = MathUtils.clamp(this.camera.skewAngle + clampedDy * 0.005, this.camera.minSkewAngle !== undefined ? this.camera.minSkewAngle : -2, 2);
        }

        this.pointerState.lastX = e.clientX;
        this.pointerState.lastY = e.clientY;

        // Also update touch state to keep them in sync
        this.touchState.lastX = e.clientX;
        this.touchState.lastY = e.clientY;
      }

      e.preventDefault();
    }

    /**
     * Handle pointer up/cancel - end drag and release capture
     * @param {PointerEvent} e - Pointer event
     */
    onPointerEnd(e) {
      if (e.pointerId === this.capturedPointerId) {
        // If drag was kept alive by pointer capture after touch died,
        // do the full cleanup here since onTouchEnd deferred it.
        const wasDragging = this.touchState.mode === 'drag' && this.touchState.cameraTouchId === null;
        this.capturedPointerId = null;
        this.pointerState = null;
        this.pendingPointerId = null;
        if (wasDragging) {
          this.touchState.mode = '';
          this.touchState.pinchTouchIds = null;
          this.touchState.pinchFirstFrame = false;
          this.touchState.dragFirstFrame = false;
          this.hideDragOverlay();
        }
      }
    }

    /**
     * Handle lost pointer capture - clean up state
     * @param {PointerEvent} e - Pointer event
     */
    onLostPointerCapture(e) {
      if (e.pointerId === this.capturedPointerId) {
        this.capturedPointerId = null;
        // Don't clear pointerState yet - we might recover
      }
    }

    /**
     * Reset all touch/pointer state and hide the drag overlay.
     * Call during mode switches to ensure a clean slate.
     */
    resetTouchState() {
      this.touchState.mode = '';
      this.touchState.cameraTouchId = null;
      this.touchState.pinchTouchIds = null;
      this.touchState.pinchFirstFrame = false;
      this.touchState.dragFirstFrame = false;
      this.touchState.lastX = null;
      this.touchState.lastY = null;
      this.touchState.startX = null;
      this.touchState.startY = null;
      this.touchState.lastCenterX = null;
      this.touchState.lastCenterY = null;
      this.pointerState = null;
      this.pendingPointerId = null;
      this.hideDragOverlay();
    }

    /**
     * Check if a touch is on a UI element that should block camera control
     * @param {Touch} touch - Touch to check
     * @returns {boolean} True if on blocking UI
     */
    isOnBlockingUI(touch) {
      const el = document.elementFromPoint(touch.clientX, touch.clientY);
      return el && el.closest('.coords, .info-panel, .gui-panel, .sidebar-panel, .top-toolbar, .top-sheet, .settings-panel, .joystick-zone, .gw-window, .gw-sidebar');
    }
    
    /**
     * Calculate angle between two touch points
     * @param {Touch} t1 - First touch
     * @param {Touch} t2 - Second touch
     * @returns {number} Angle in degrees
     */
    getTouchAngle(t1, t2) {
      return Math.atan2(t2.clientY - t1.clientY, t2.clientX - t1.clientX) * 180 / Math.PI;
    }

    /**
     * Get touches that are NOT being used by the joystick
     * Note: Only filters out ACTIVE joystick touches, not pending ones.
     * Pending touches are still treated as camera touches until joystick activates.
     * @param {TouchList} touches - All current touches
     * @returns {Touch[]} Array of non-joystick touches
     */
    getNonJoystickTouches(touches) {
      // Only filter out the touch if joystick is ACTIVE (not pending)
      const joystickTouchId = this.virtualJoystick?.isActive() ? this.virtualJoystick.getTouchId() : null;
      const containerEl = this.containerEl;
      const result = [];
      for (let i = 0; i < touches.length; i++) {
        const touch = touches[i];
        if (touch.identifier === joystickTouchId) continue;
        // Filter out touches that started on elements outside the container
        // (e.g., action buttons, HUD quickbar, toolbars on document.body).
        // e.touches includes ALL document touches, but we only want container ones.
        if (containerEl && touch.target && !containerEl.contains(touch.target)) continue;
        result.push(touch);
      }
      return result;
    }

    /**
     * Called by VirtualJoystick when it activates after the pending delay.
     * This allows us to update camera state since that touch is no longer available.
     * @param {number} touchId - The touch ID that joystick claimed
     */
    onJoystickActivated(touchId) {
      // If we were using this touch for camera control, reset to find another touch or clear mode
      if ((this.touchState.mode === 'drag' || this.touchState.mode === 'potential_drag') && this.touchState.cameraTouchId === touchId) {
        this.touchState.mode = '';
        this.touchState.cameraTouchId = null;
      }
      // If this touch was part of a pinch, the pinch handling will adapt on next move
      // because it always uses the first two available cameraTouches
    }

    /**
     * Check if a touch is in the joystick zone
     * @param {Touch} touch - Touch to check
     * @returns {boolean} True if touch is in joystick zone
     */
    isInJoystickZone(touch) {
      const joystickZone = document.querySelector('.joystick-zone');
      if (!joystickZone) return false;
      const rect = joystickZone.getBoundingClientRect();
      return touch.clientX >= rect.left && touch.clientX <= rect.right &&
             touch.clientY >= rect.top && touch.clientY <= rect.bottom;
    }

    /**
     * Initialize pinch state with given touches
     * @param {Touch} t1 - First touch
     * @param {Touch} t2 - Second touch
     */
    initPinchState(t1, t2) {
      // Clear any single-finger state first
      this.touchState.cameraTouchId = null;
      this.touchState.lastX = null;
      this.touchState.lastY = null;
      this.touchState.startX = null;
      this.touchState.startY = null;

      // Set pinch state
      this.touchState.mode = 'pinch';
      this.touchState.pinchTouchIds = [t1.identifier, t2.identifier];
      this.touchState.initialDistance = Math.hypot(t2.clientX - t1.clientX, t2.clientY - t1.clientY);
      this.touchState.initialZoom = this.camera.zoomLevel;
      this.touchState.lastCenterX = (t1.clientX + t2.clientX) / 2;
      this.touchState.lastCenterY = (t1.clientY + t2.clientY) / 2;

      // Mark that we need to skip first frame of pan/rotation to avoid jumps
      this.touchState.pinchFirstFrame = true;
    }

    /**
     * Handle touch start event
     * @param {TouchEvent} e - Touch event
     */
    onTouchStart(e) {
      // Skip if on blocking UI
      const firstTouch = e.changedTouches[0];
      if (firstTouch && this.isOnBlockingUI(firstTouch)) return;

      // Get camera touches (non-joystick)
      const cameraTouches = this.getNonJoystickTouches(e.touches);

      // RECOVERY: If we're in active drag mode and receiving a new touchstart,
      // this is likely touch re-targeting after DOM change (subdivision).
      // Continue the drag with the new touch instead of resetting.
      if (this.touchState.mode === 'drag' && cameraTouches.length >= 1) {
        const touch = cameraTouches[0];
        // Update to track the new touch
        this.touchState.cameraTouchId = touch.identifier;
        // Skip first frame to avoid position jump
        this.touchState.dragFirstFrame = true;
        this.touchState.lastX = touch.clientX;
        this.touchState.lastY = touch.clientY;
        e.preventDefault();
        return;
      }

      // RECOVERY: If we're in potential_drag mode and receiving another touchstart,
      // update to track the new touch (DOM may have changed before drag confirmed)
      if (this.touchState.mode === 'potential_drag' && cameraTouches.length >= 1) {
        const touch = cameraTouches[0];
        this.touchState.cameraTouchId = touch.identifier;
        this.touchState.lastX = touch.clientX;
        this.touchState.lastY = touch.clientY;
        // Keep startX/startY for drag confirmation threshold
        e.preventDefault();
        return;
      }

      // Prevent default for multi-touch to block browser pinch-zoom
      if (cameraTouches.length >= 2) {
        e.preventDefault();
        this.showDragOverlay(); // Show overlay for pinch
        this.initPinchState(cameraTouches[0], cameraTouches[1]);
        return;
      }

      if (cameraTouches.length === 1 && !this.touchState.mode) {
        // Single finger: potential drag (not confirmed until movement)
        const touch = cameraTouches[0];
        this.touchState.mode = 'potential_drag';
        this.touchState.cameraTouchId = touch.identifier;
        this.touchState.startX = touch.clientX;
        this.touchState.startY = touch.clientY;
        this.touchState.lastX = touch.clientX;
        this.touchState.lastY = touch.clientY;
      }
    }

    /**
     * Check if camera is actively being controlled (not just potential)
     * @returns {boolean} True if camera is in active drag or pinch mode
     */
    isCameraActive() {
      return this.touchState.mode === 'drag' || this.touchState.mode === 'pinch';
    }
    
    /**
     * Handle touch move event
     * @param {TouchEvent} e - Touch event
     */
    onTouchMove(e) {
      // Track when we last received a touch move (for pointer event fallback logic)
      this.lastTouchMoveTime = performance.now();

      // Get camera touches (non-joystick)
      const cameraTouches = this.getNonJoystickTouches(e.touches);

      // Skip if no relevant touches
      if (cameraTouches.length === 0) return;

      // Robust mode transition: if we have 2+ camera touches but aren't in pinch mode, switch to pinch
      // This handles edge cases where touchstart events don't properly transition modes
      if (cameraTouches.length >= 2 && this.touchState.mode !== 'pinch') {
        this.initPinchState(cameraTouches[0], cameraTouches[1]);
      }

      // Handle pinch mode
      if (this.touchState.mode === 'pinch' && cameraTouches.length >= 2) {
        // Always use first two camera touches - simpler and more robust
        // Touch ordering is consistent (by start time), so this gives stable results
        const t1 = cameraTouches[0];
        const t2 = cameraTouches[1];

        // Check if we need to reinitialize (touches changed)
        const touchesChanged = !this.touchState.pinchTouchIds ||
          this.touchState.pinchTouchIds[0] !== t1.identifier ||
          this.touchState.pinchTouchIds[1] !== t2.identifier;

        if (touchesChanged) {
          // Reinitialize pinch state with new touches
          this.touchState.pinchTouchIds = [t1.identifier, t2.identifier];
          this.touchState.initialDistance = Math.hypot(t2.clientX - t1.clientX, t2.clientY - t1.clientY);
          this.touchState.initialZoom = this.camera.zoomLevel;
          this.touchState.lastCenterX = (t1.clientX + t2.clientX) / 2;
          this.touchState.lastCenterY = (t1.clientY + t2.clientY) / 2;
          this.touchState.pinchFirstFrame = true;
          e.preventDefault();
          return;
        }

        // Skip first frame of pinch to avoid jumps from mode transition
        if (this.touchState.pinchFirstFrame) {
          this.touchState.pinchFirstFrame = false;
          // Update last values to current positions
          this.touchState.lastCenterX = (t1.clientX + t2.clientX) / 2;
          this.touchState.lastCenterY = (t1.clientY + t2.clientY) / 2;
          this.touchState.initialDistance = Math.hypot(t2.clientX - t1.clientX, t2.clientY - t1.clientY);
          this.touchState.initialZoom = this.camera.zoomLevel;
          e.preventDefault();
          return;
        }

        // Pinch zoom
        const currentDistance = Math.hypot(t2.clientX - t1.clientX, t2.clientY - t1.clientY);
        const scaleFactor = currentDistance / this.touchState.initialDistance;
        const newZoom = MathUtils.clamp(
          this.touchState.initialZoom * scaleFactor,
          this.camera.MIN_ZOOM,
          this.camera.MAX_ZOOM
        );
        if (this.orbitalController && this.orbitalController.isEnabled()) {
          this.orbitalController.setTargetZoom(newZoom);
        } else {
          this.camera.zoomLevel = newZoom;
        }

        // Two-finger pan controls rotation and skew (like single-finger drag)
        const centerX = (t1.clientX + t2.clientX) / 2;
        const centerY = (t1.clientY + t2.clientY) / 2;
        const dx = centerX - this.touchState.lastCenterX;
        const dy = centerY - this.touchState.lastCenterY;

        // Horizontal pan = rotate camera, vertical pan = adjust skew
        if (this.orbitalController && this.orbitalController.isEnabled()) {
          this.orbitalController.targetOrbitAngle -= dx * 0.5;
        } else {
          this.camera.angle -= dx * 0.5;
        }
        this.camera.skewAngle = MathUtils.clamp(this.camera.skewAngle + dy * 0.005, this.camera.minSkewAngle !== undefined ? this.camera.minSkewAngle : -2, 2);

        this.touchState.lastCenterX = centerX;
        this.touchState.lastCenterY = centerY;

        e.preventDefault();
        return;
      }

      // Handle potential drag → confirm as drag when finger moves
      // But don't steal touch from FacePicker during an active long-press on a face
      if (this.touchState.mode === 'potential_drag' && cameraTouches.length === 1) {
        const facePicker = window.game?.facePicker;
        const faceHasLongPress = facePicker?.isLongPressActive;

        const touch = cameraTouches[0];
        if (touch.identifier === this.touchState.cameraTouchId) {
          const movedX = Math.abs(touch.clientX - this.touchState.startX);
          const movedY = Math.abs(touch.clientY - this.touchState.startY);
          // Confirm as drag after small movement threshold
          // If FacePicker has an active long-press, wait for it to cancel (at 10px) first
          if ((movedX > 5 || movedY > 5) && !faceHasLongPress) {
            this.touchState.mode = 'drag';
            this.touchState.lastX = touch.clientX;
            this.touchState.lastY = touch.clientY;
            this.touchState.dragFirstFrame = false;
            // Show overlay to capture touch events during drag
            this.showDragOverlay();
            e.preventDefault();
          }
        }
      }

      // Handle single-finger drag mode
      if (this.touchState.mode === 'drag') {
        // Find our tracked camera touch
        let touch = null;
        for (const t of cameraTouches) {
          if (t.identifier === this.touchState.cameraTouchId) {
            touch = t;
            break;
          }
        }

        // RECOVERY: If we can't find our touch but have available touches,
        // the touch may have been re-targeted after DOM change (subdivision).
        // Re-acquire the first available touch and continue the drag.
        if (!touch && cameraTouches.length >= 1) {
          touch = cameraTouches[0];
          this.touchState.cameraTouchId = touch.identifier;
          // Skip first frame to avoid position jump
          this.touchState.dragFirstFrame = true;
          this.touchState.lastX = touch.clientX;
          this.touchState.lastY = touch.clientY;
        }

        if (touch) {
          // Skip first frame of drag to avoid jumps from mode transition
          if (this.touchState.dragFirstFrame || this.touchState.lastX === null) {
            this.touchState.dragFirstFrame = false;
            this.touchState.lastX = touch.clientX;
            this.touchState.lastY = touch.clientY;
            e.preventDefault();
            return;
          }

          const dx = touch.clientX - this.touchState.lastX;
          const dy = touch.clientY - this.touchState.lastY;

          // Cap delta to prevent wild rotation from stale state or edge cases
          const MAX_DELTA = 40;
          const clampedDx = MathUtils.clamp(dx, -MAX_DELTA, MAX_DELTA);
          const clampedDy = MathUtils.clamp(dy, -MAX_DELTA, MAX_DELTA);

          // Rotate camera horizontally and adjust skew vertically
          if (this.orbitalController && this.orbitalController.isEnabled()) {
            this.orbitalController.targetOrbitAngle -= clampedDx * 0.5;
          } else {
            this.camera.angle -= clampedDx * 0.5;
          }
          this.camera.skewAngle = MathUtils.clamp(this.camera.skewAngle + clampedDy * 0.005, this.camera.minSkewAngle !== undefined ? this.camera.minSkewAngle : -2, 2);

          this.touchState.lastX = touch.clientX;
          this.touchState.lastY = touch.clientY;
          e.preventDefault();
        }
      }
    }

    /**
     * Handle touch end event
     * @param {TouchEvent} e - Touch event
     */
    onTouchEnd(e) {
      // Get remaining camera touches (non-joystick)
      const cameraTouches = this.getNonJoystickTouches(e.touches);

      if (cameraTouches.length === 0) {
        // No camera touches remaining.
        // If we have active pointer capture and were dragging, keep the drag alive
        // via pointer events. This handles touchcancel from DOM changes during sort
        // (e.g., cuboid subdivision removing the original touch target).
        if (this.touchState.mode === 'drag' && this.capturedPointerId !== null) {
          // Touch died but pointer capture is active — let pointer events continue.
          // Clear touch-specific state but preserve drag mode and overlay.
          this.touchState.cameraTouchId = null;
          return;
        }
        // Otherwise fully reset
        this.touchState.mode = '';
        this.touchState.cameraTouchId = null;
        this.touchState.pinchTouchIds = null;
        this.touchState.pinchFirstFrame = false;
        this.touchState.dragFirstFrame = false;
        this.hideDragOverlay();
      } else if (cameraTouches.length === 1) {
        // Single camera touch remaining - switch to drag mode
        const touch = cameraTouches[0];
        this.touchState.mode = 'drag';
        this.touchState.cameraTouchId = touch.identifier;
        this.touchState.lastX = touch.clientX;
        this.touchState.lastY = touch.clientY;
        this.touchState.pinchTouchIds = null;
        this.touchState.dragFirstFrame = true;
      } else if (cameraTouches.length >= 2) {
        // Still have 2+ camera touches - reinitialize pinch
        const t1 = cameraTouches[0], t2 = cameraTouches[1];
        this.touchState.mode = 'pinch';
        this.touchState.pinchTouchIds = [t1.identifier, t2.identifier];
        this.touchState.initialDistance = Math.hypot(t2.clientX - t1.clientX, t2.clientY - t1.clientY);
        this.touchState.initialZoom = this.camera.zoomLevel;
        this.touchState.lastCenterX = (t1.clientX + t2.clientX) / 2;
        this.touchState.pinchFirstFrame = true;
        this.touchState.lastCenterY = (t1.clientY + t2.clientY) / 2;
        this.touchState.lastAngle = this.getTouchAngle(t1, t2);
      }
    }

    /**
     * Handle touch cancel event
     * @param {TouchEvent} e - Touch event
     */
    onTouchCancel(e) {
      this.onTouchEnd(e);
    }

    /**
     * Update camera based on current input state
     * @param {number} deltaTime - Time since last update in seconds
     */
    update(deltaTime = 1/60) {
      // Check if orbital camera mode is active
      if (this.orbitalController && this.orbitalController.isEnabled()) {
        // Orbital mode: O/P orbit around target, WASD controls character (handled by CharacterController)
        if (this.keys.o) this.orbitalController.orbit('left', deltaTime);
        if (this.keys.p) this.orbitalController.orbit('right', deltaTime);

        // Zoom controls still work in orbital mode (smooth)
        if (this.keys.zoomIn) {
          const zoomStep = this.camera.ZOOM_STEP * deltaTime;
          this.orbitalController.setTargetZoom(this.orbitalController.targetZoomLevel + zoomStep);
        }
        if (this.keys.zoomOut) {
          const zoomStep = this.camera.ZOOM_STEP * deltaTime;
          this.orbitalController.setTargetZoom(this.orbitalController.targetZoomLevel - zoomStep);
        }

        // Skew controls still work in orbital mode
        if (this.keys.ArrowUp) this.camera.adjustSkew('up', deltaTime);
        if (this.keys.ArrowDown) this.camera.adjustSkew('down', deltaTime);
      } else {
        // Free camera mode (existing behavior)
        // Camera movement (A/D strafe left/right)
        if(this.keys.a) this.camera.move('left', deltaTime);
        if(this.keys.d) this.camera.move('right', deltaTime);

        // Forward/back movement (Spacebar = forward, C = backward)
        if(this.keys[' ']) this.camera.move('up', deltaTime);
        if(this.keys.c) this.camera.move('down', deltaTime);

        // Zoom controls (W/S and +/- all zoom)
        if(this.keys.w || this.keys.zoomIn) this.camera.zoom('in', deltaTime);
        if(this.keys.s || this.keys.zoomOut) this.camera.zoom('out', deltaTime);

        // Rotation controls (O = left, P = right)
        if(this.keys.o) this.camera.rotate('left', deltaTime);
        if(this.keys.p) this.camera.rotate('right', deltaTime);

        // Skew controls
        if(this.keys.ArrowUp) this.camera.adjustSkew('up', deltaTime);
        if(this.keys.ArrowDown) this.camera.adjustSkew('down', deltaTime);
      }
    }

    /**
     * Set the orbital camera controller reference
     * @param {OrbitalCameraController} controller - The orbital camera controller
     */
    setOrbitalController(controller) {
      this.orbitalController = controller;
    }
  }